import { ctRefreshTokenRepository, CtTokenRepository } from 'common/api/models/ct-token.repository';
import { ctTokenPayloadModel } from 'common/api/models/ct-token';
import { fetchCtTokenApi } from 'common/api/login';
import { refreshCtTokenApi } from 'common/api/users';
import { doFetch } from 'utils/api';

import { isCtTokenExpired } from 'utils';

const removeTokenFromLocal = () => {
  CtTokenRepository.delete();
  ctRefreshTokenRepository.delete();
  //force page reload without ctToken which will start fresh auth against KeyCloak or if public site just render app
  window.location.reload(true);
};

/**
 * Refresh token & set new token in token repository
 * @param {string|null} ctRefreshToken
 * @param {number} userId
 */
export const refreshCtToken = (ctRefreshToken, userId) => {
  return refreshCtTokenApi(ctRefreshToken, userId)
    .then((response) => {
      // 204 CT is configured as public website - clear ctToken from repo and reload
      if (response.status === 204) {
        removeTokenFromLocal();
      }
      return response;
    })
    .catch((error) => {
      // 403 refresh token ins't valid anymore, 404 BE can not find refresh token  - go to KeyCloak and fresh login
      if (error.status === 403 || error.status === 404) {
        removeTokenFromLocal();
      }

      return Promise.reject(error);
    });
};

/**
 * Fetch ctToken from api and set in token repository, then render app
 *
 * @param {string} token - keycloak access token
 * @return {Promise<number>}
 */
export const fetchCtToken = (token) => {
  return fetchCtTokenApi(token).then((ctToken) => {
    CtTokenRepository.persist(ctToken);
    ctRefreshTokenRepository.persist(ctToken);
  });
};

/**
 * @param {string|null} ctToken - JWT token itself
 * @return {*}
 */
export const extractTokenPayload = (ctToken = CtTokenRepository.fetch()) => {
  if (!ctToken) {
    return null;
  }
  return ctTokenPayloadModel(JSON.parse(atob(ctToken.split('.')[1])));
};

/**
 * Decorator method around doFetch()
 *
 * It will refresh token before execution
 *
 * @param {string} endpoint
 * @param {*} options - native fetch() api options object
 * @param {string} refreshToken
 * @param {number} userId
 * @return {Promise<T>} returns doFetch() Promise
 */
export const doFetchExpiredToken = (endpoint, options, refreshToken, userId) => {
  return refreshCtToken(refreshToken, userId).then((newToken) => {
    CtTokenRepository.persist(newToken);

    options.headers = {
      ...options.headers,
      ...{
        Authorization: `Bearer ${newToken.ctToken}`,
        Accept: 'application/json',
      },
    };

    return doFetch(endpoint, options);
  });
};

/**
 * Check CT token and refresh if needed
 * @param {string} token - keycloak access token
 * @return {Promise<number>}
 */
export const authenticateCtUser = async (token) => {
  const ctToken = CtTokenRepository.fetch();

  if (ctToken) {
    const ctTokenPayload = extractTokenPayload();
    const ctRefreshToken = ctRefreshTokenRepository.fetch();

    return isCtTokenExpired(ctTokenPayload) && refreshCtToken(ctRefreshToken, ctTokenPayload.user_id);
  } else {
    return fetchCtToken(token);
  }
};
